/**
 * Component, which takes care of options bar in StoreFinder
 *
 * @author Tamara Bazko
 **/

import $ from 'jquery';
import createLogger from 'components/logger/Logger';
import Singleton from 'components/utils/Singleton';
import EventTypes from 'components/EventTypes';
import matchBreakpoint from 'components/utils/matchBreakpoint';
import StoreFinderComponent from './StoreFinderComponent';
import 'jquery.extendPrototype';

const Logger = createLogger('OptionsBar');

function OptionsBar(settings) {
  Logger.info('Created');
  StoreFinderComponent.call(this, settings, module);

  this.$optionsBar = this.$element;
  this.$container = this.$optionsBar.closest('.js-storeFinder-bar');
  this.$checkbox = this.findStoreFinderElement('.js-storeFinder-bar-listTriggerCheckbox');
  this.$checkboxLabel = this.findElement(this.settings.checkboxLabel);
  this.$filterTrigger = this.findElement(this.settings.filterTrigger);
  this.$listTrigger = this.findElement(this.settings.listTrigger);

  // Initialize
  this.isVisible = this.$checkbox.prop('checked');
  this.bindEvents();
}

$.extendPrototype(OptionsBar, StoreFinderComponent.prototype, {
  $optionsBar: undefined,
  $container: undefined,
  $checkbox: undefined,
  $checkboxLabel: undefined,
  $filterTrigger: undefined,
  $listTrigger: undefined,
  settings: {
    checkboxLabel: '.js-storeFinder-bar-listTriggerLabel',
    filterTrigger: '.js-storeFinder-bar-triggerFilter',
    listTrigger: '.js-storeFinder-bar-triggerFilter',
    selector: '.js-storeFinder-bar-optionsBar',
  },

  /**
   * Bind events
   */
  bindEvents() {
    this.$checkbox.on('change', this.onListVisibilityChange.bind(this));
    this.$listTrigger.on('vclick', this.onListButtonClick.bind(this));

    document.jq
      .on(
        EventTypes.STOREFINDER_SEARCH_LOCATION_BUTTON_CLICKED,
        this.onSearchLocationButtonClick.bind(this)
      )
      .on(EventTypes.STOREFINDER_HIDE_LIST_REQUEST, this.setListVisibility.bind(this, false))
      .on(EventTypes.STOREFINDER_SHOW_LIST_REQUEST, this.setListVisibility.bind(this, true))
      .on(EventTypes.STOREFINDER_STORE_ACTIVATED, this.onStoreSelected.bind(this, true))
      .on(EventTypes.STOREFINDER_STORE_DESACTIVATED, this.onStoreSelected.bind(this, false))
      .on(EventTypes.STOREFINDER_STORES_FILTERED, this.setFilterTriggerActive.bind(this))
      .on(EventTypes.STOCKLOCATOR_TOGGLE_MESSAGING, this.toggleStockLocatorMessaging.bind(this));
  },

  /**
   * Event listener for when user clicks on list button.
   *
   * @param  {object} event jQuery event object
   */
  onListButtonClick(event) {
    document.jq.trigger(EventTypes.STOREFINDER_SHOW_FILTERS_REQUEST);

    event.preventDefault();
    event.stopPropagation();
  },

  /**
   * Event listener for when user clicks on search location button
   */
  onSearchLocationButtonClick() {
    const selects = document.querySelectorAll('.js-storeFinder .js-selectSize');
    selects.forEach(select => select.jq.trigger('change'));
  },

  /**
   * On change, toggle store list visibility
   */
  onListVisibilityChange() {
    this.isVisible = this.$checkbox.prop('checked');
    Logger.info('onListVisibilityChange', this.isVisible);

    if (!matchBreakpoint(this.settings.breakpointOverlay)) {
      this.$optionsBar.trigger(
        this.isVisible
          ? EventTypes.STOREFINDER_HIDE_MAP_REQUEST
          : EventTypes.STOREFINDER_SHOW_MAP_REQUEST
      );
    }
  },

  onStoreSelected(value) {
    this.$container.toggleClass('is-hidden', value);
  },

  /**
   * Set visibility of list
   * @param {Boolean} isVisible Should list be visible?
   */
  setListVisibility(isVisible) {
    Logger.info('setListVisibility', isVisible);
    this.$container.toggleClass('is-hidden', !isVisible);
    this.$checkbox.prop('checked', isVisible);
    this.isVisible = this.$checkbox.prop('checked');
  },

  /**
   * Toggle stock locator messaging based on parameters
   */
  toggleStockLocatorMessaging(event, state, error) {
    state = state || false;
    error = error || false;

    this.$container
      .find('.js-stockLocator--please-select')
      .toggleClass('error', error)
      .toggleClass('is-hidden', !state);

    this.$container
      .find('.js-storeFinder-bar-scrollArea-title')
      .toggleClass('is-active', !state)
      .toggleClass('is-hidden', state);
  },

  /**
   * Set filter trigger to correct state
   * @param  {object} event     jQuery event object
   * @param  {object} eventData Data  Array with filters
   */
  setFilterTriggerActive(event, eventData) {
    if (typeof eventData.filters === 'undefined') {
      this.$filterTrigger.removeClass('is-filtered');
    } else {
      this.$filterTrigger.addClass('is-filtered');
    }
  },
});

export default Singleton.create(OptionsBar, 'OptionsBar');
